import axios from "axios";

export interface PickedImageData {
  id: number;
  url: string;
  width: number;
  height: number;
  thumbnail: string;
  thumbWidth: number;
  thumbHeight: number;
  filename: string;
  aspect: "landscape" | "portrait";
}

export interface ImageSelectedDetail {
  imageData: PickedImageData;
}

type AspectFilter = "all" | "landscape" | "portrait";

export class ImagePickerDrawer extends EventTarget {
  el: HTMLElement;
  private _grid: HTMLElement;
  private _searchInput: HTMLInputElement;
  private _aspectBtns: HTMLButtonElement[] = [];
  private _allImages: PickedImageData[] = [];
  private _fetched: boolean = false;
  private _aspectFilter: AspectFilter = "all";

  constructor() {
    super();

    const drawer = document.createElement("div");
    drawer.className = "image-picker-drawer";

    const header = document.createElement("div");
    header.className = "image-picker-drawer__header";

    // const title = document.createElement("p");
    // title.className = "image-picker-drawer__title";
    // title.textContent = "Swap image";

    this._searchInput = document.createElement("input");
    this._searchInput.type = "text";
    this._searchInput.placeholder = "Search…";
    this._searchInput.className = "image-picker-drawer__search";
    this._searchInput.addEventListener("input", () => this.renderImages());

    const aspectToggle = this.createAspectToggle();

    const closeBtn = document.createElement("button");
    closeBtn.className = "image-picker-drawer__close";
    closeBtn.innerHTML = `<svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256"><rect width="256" height="256" fill="none"/><line x1="200" y1="56" x2="56" y2="200" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="24"/><line x1="200" y1="200" x2="56" y2="56" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="24"/></svg>`;
    closeBtn.addEventListener("click", () => this.close());

    // header.appendChild(title);
    header.appendChild(this._searchInput);
    header.appendChild(aspectToggle);
    header.appendChild(closeBtn);

    this._grid = document.createElement("div");
    this._grid.className = "image-picker-drawer__grid";

    drawer.appendChild(header);
    drawer.appendChild(this._grid);
    document.body.appendChild(drawer);
    this.el = drawer;
  }

  private createAspectToggle(): HTMLElement {
    const group = document.createElement("div");
    group.className = "image-picker-drawer__aspect-toggle";

    const options: { label: string; value: AspectFilter }[] = [
      { label: "All", value: "all" },
      { label: `<span class="aspect-shape aspect-shape--landscape"></span>`, value: "landscape" },
      { label: `<span class="aspect-shape aspect-shape--portrait"></span>`, value: "portrait" },
    ];

    options.forEach(({ label, value }) => {
      const btn = document.createElement("button");
      btn.innerHTML = label;
      btn.className = "image-picker-drawer__aspect-btn";
      btn.addEventListener("click", () => {
        this._aspectFilter = value;
        this.updateAspectBtns();
        this.renderImages();
      });
      this._aspectBtns.push(btn);
      group.appendChild(btn);
    });

    this.updateAspectBtns();
    return group;
  }

  private updateAspectBtns(): void {
    const values: AspectFilter[] = ["all", "landscape", "portrait"];
    this._aspectBtns.forEach((btn, i) => {
      btn.classList.toggle("is-active", values[i] === this._aspectFilter);
    });
  }

  open(): void {
    this._searchInput.value = "";
    this.el.classList.add("is-open");
    document.body.style.overflow = "hidden";

    if (!this._fetched) {
      this.fetchImages();
    } else {
      this.renderImages();
    }
  }

  close(): void {
    this.el.classList.remove("is-open");
    document.body.style.overflow = "";
  }

  private async fetchImages(): Promise<void> {
    this._grid.innerHTML = `<p class="image-picker-drawer__status">Loading…</p>`;

    const { nonce } = window.SuburbiaConfig || {};

    try {
      const response = await axios.get(`/wp-json/cwf/v1/archive/images`, {
        headers: { "X-WP-Nonce": nonce || "" },
      });
      this._allImages = response.data;
      this._fetched = true;
      this.renderImages();
    } catch {
      this._grid.innerHTML = `<p class="image-picker-drawer__status">Failed to load images.</p>`;
    }
  }

  private renderImages(): void {
    const term = this._searchInput.value.toLowerCase().trim();

    let images = this._aspectFilter !== "all"
      ? this._allImages.filter(img => img.aspect === this._aspectFilter)
      : this._allImages;

    if (term) {
      images = images.filter(img => img.filename.toLowerCase().includes(term));
    }

    this._grid.innerHTML = "";

    if (!images.length) {
      this._grid.innerHTML = `<p class="image-picker-drawer__status">No images found.</p>`;
      return;
    }

    images.forEach(img => {
      const btn = document.createElement("button");
      btn.className = "image-picker-drawer__item";
      btn.title = img.filename;
      btn.innerHTML = `<img src="${img.thumbnail}" alt="" loading="lazy" width="${img.thumbWidth}" height="${img.thumbHeight}">`;
      btn.addEventListener("click", () => {
        this.dispatchEvent(new CustomEvent<ImageSelectedDetail>("imageSelected", {
          detail: { imageData: img },
        }));
        this.close();
      });
      this._grid.appendChild(btn);
    });
  }
}
